/**
 * Centralized Role Mapping and Validation System
 * 
 * This utility ensures consistent role handling across the entire application
 * and prevents access denied issues by providing fallbacks and validation.
 */

// Define all possible user roles in the system
export const USER_ROLES = {
  // Client portal roles
  MAIN_USER: 'main_user',
  ADDITIONAL_USER: 'additional_user', 
  USER: 'user', // Legacy role that maps to client portal
  
  // Staff portal roles
  ADMIN: 'admin',
  STAFF: 'staff',
  
  // Additional roles for future expansion
  MANAGER: 'manager',
  SUPPORT: 'support',
  FINANCE: 'finance'
};

// Define role groups for easier validation
export const ROLE_GROUPS = {
  CLIENT_PORTAL: [USER_ROLES.MAIN_USER, USER_ROLES.ADDITIONAL_USER, USER_ROLES.USER],
  STAFF_PORTAL: [USER_ROLES.ADMIN, USER_ROLES.STAFF, USER_ROLES.MANAGER, USER_ROLES.SUPPORT, USER_ROLES.FINANCE],
  ADMIN_ONLY: [USER_ROLES.ADMIN],
  MANAGER_AND_ABOVE: [USER_ROLES.ADMIN, USER_ROLES.MANAGER]
};

// Role mapping for backward compatibility and normalization
export const ROLE_MAPPING = {
  // Map legacy roles to current roles
  'user': USER_ROLES.USER,
  'main_user': USER_ROLES.MAIN_USER,
  'additional_user': USER_ROLES.ADDITIONAL_USER,
  'admin': USER_ROLES.ADMIN,
  'staff': USER_ROLES.STAFF,
  
  // Ensure consistency
  [USER_ROLES.USER]: USER_ROLES.USER,
  [USER_ROLES.MAIN_USER]: USER_ROLES.MAIN_USER,
  [USER_ROLES.ADDITIONAL_USER]: USER_ROLES.ADDITIONAL_USER,
  [USER_ROLES.ADMIN]: USER_ROLES.ADMIN,
  [USER_ROLES.STAFF]: USER_ROLES.STAFF
};

/**
 * Normalize a role to ensure consistency
 * @param {string} role - The role to normalize
 * @returns {string} - Normalized role
 */
export const normalizeRole = (role) => {
  if (!role || typeof role !== 'string') {
    console.warn('⚠️ Invalid role provided:', role);
    return USER_ROLES.USER; // Default fallback to client portal
  }
  
  const normalizedRole = ROLE_MAPPING[role.toLowerCase()] || ROLE_MAPPING[role];
  
  if (!normalizedRole) {
    console.warn('⚠️ Unknown role detected:', role, '- defaulting to USER');
    return USER_ROLES.USER; // Default fallback to client portal
  }
  
  return normalizedRole;
};

/**
 * Check if a user has access to client portal
 * @param {string} role - The user's role
 * @returns {boolean} - True if user can access client portal
 */
export const canAccessClientPortal = (role) => {
  const normalizedRole = normalizeRole(role);
  return ROLE_GROUPS.CLIENT_PORTAL.includes(normalizedRole);
};

/**
 * Check if a user has access to staff portal
 * @param {string} role - The user's role
 * @returns {boolean} - True if user can access staff portal
 */
export const canAccessStaffPortal = (role) => {
  const normalizedRole = normalizeRole(role);
  return ROLE_GROUPS.STAFF_PORTAL.includes(normalizedRole);
};

/**
 * Check if a user has admin privileges
 * @param {string} role - The user's role
 * @returns {boolean} - True if user has admin privileges
 */
export const isAdmin = (role) => {
  const normalizedRole = normalizeRole(role);
  return ROLE_GROUPS.ADMIN_ONLY.includes(normalizedRole);
};

/**
 * Check if a user has manager privileges or above
 * @param {string} role - The user's role
 * @returns {boolean} - True if user has manager privileges or above
 */
export const isManagerOrAbove = (role) => {
  const normalizedRole = normalizeRole(role);
  return ROLE_GROUPS.MANAGER_AND_ABOVE.includes(normalizedRole);
};

/**
 * Get the appropriate portal URL for a user based on their role
 * @param {string} role - The user's role
 * @returns {string} - The portal URL the user should access
 */
export const getPortalUrl = (role) => {
  const normalizedRole = normalizeRole(role);
  
  if (canAccessStaffPortal(normalizedRole)) {
    return '/staff';
  }
  
  if (canAccessClientPortal(normalizedRole)) {
    return '/portal';
  }
  
  // Default fallback - always allow client portal access
  console.warn('⚠️ Role does not match any portal group:', role, '- defaulting to client portal');
  return '/portal';
};

/**
 * Get user-friendly role display name
 * @param {string} role - The user's role
 * @returns {string} - User-friendly role name
 */
export const getRoleDisplayName = (role) => {
  const normalizedRole = normalizeRole(role);
  
  const displayNames = {
    [USER_ROLES.USER]: 'User',
    [USER_ROLES.MAIN_USER]: 'Main User',
    [USER_ROLES.ADDITIONAL_USER]: 'Additional User',
    [USER_ROLES.ADMIN]: 'Administrator',
    [USER_ROLES.STAFF]: 'Staff Member',
    [USER_ROLES.MANAGER]: 'Manager',
    [USER_ROLES.SUPPORT]: 'Support',
    [USER_ROLES.FINANCE]: 'Finance'
  };
  
  return displayNames[normalizedRole] || 'User';
};

/**
 * Validate user role and provide fallback
 * @param {Object} user - User object
 * @returns {Object} - User object with validated role
 */
export const validateUserRole = (user) => {
  if (!user) {
    console.warn('⚠️ No user object provided');
    return { role: USER_ROLES.USER, isValid: false };
  }
  
  const originalRole = user.role;
  const normalizedRole = normalizeRole(originalRole);
  
  // Log role changes for debugging
  if (originalRole !== normalizedRole) {
    console.info('🔄 Role normalized:', originalRole, '→', normalizedRole);
  }
  
  return {
    ...user,
    role: normalizedRole,
    originalRole: originalRole,
    isValid: true,
    canAccessClientPortal: canAccessClientPortal(normalizedRole),
    canAccessStaffPortal: canAccessStaffPortal(normalizedRole),
    isAdmin: isAdmin(normalizedRole),
    portalUrl: getPortalUrl(normalizedRole),
    roleDisplayName: getRoleDisplayName(normalizedRole)
  };
};

/**
 * Emergency fallback - ensures user always has access somewhere
 * @param {string} role - The user's role
 * @returns {Object} - Access information with fallbacks
 */
export const getEmergencyAccess = (role) => {
  const normalizedRole = normalizeRole(role);
  
  return {
    role: normalizedRole,
    canAccessClientPortal: true, // Always allow client portal access
    canAccessStaffPortal: canAccessStaffPortal(normalizedRole),
    portalUrl: canAccessStaffPortal(normalizedRole) ? '/staff' : '/portal',
    isEmergencyFallback: !ROLE_GROUPS.CLIENT_PORTAL.includes(normalizedRole) && !ROLE_GROUPS.STAFF_PORTAL.includes(normalizedRole)
  };
};

// Export all utilities
export default {
  USER_ROLES,
  ROLE_GROUPS,
  ROLE_MAPPING,
  normalizeRole,
  canAccessClientPortal,
  canAccessStaffPortal,
  isAdmin,
  isManagerOrAbove,
  getPortalUrl,
  getRoleDisplayName,
  validateUserRole,
  getEmergencyAccess
};
